import type {
  CorretajeCaseCode,
  CorretajeCommissionScheme,
  CorretajeSplitResult,
} from "../types";

/**
 * Almikia Epic EC (TASK-028): pure mirror of the SQL function
 * `public.corretaje_commission_split` (02_functions.sql) so the FakeRest demo
 * computes the same split as the database. Keep the case resolution, the
 * rounding and the error messages identical to the SQL. The percentages live
 * in the scheme rows passed in; nothing is hardcoded here.
 */

export type ColegaFoundBy =
  | "asesora_proactiva"
  | "lead_manager"
  | "direccion_corretaje";

export type CorretajeSplitInput = {
  sharedWithColega: boolean;
  /** Required only for a shared sale of an Almikia advisor-inventory property. */
  colegaFoundBy?: ColegaFoundBy | null;
  propertyOwner: "almikia" | "colega";
  opcionadoraKind: "advisor" | "amk";
  sameAdvisor: boolean;
  /** Total commission C. */
  total: number;
  /** Reference date, ISO YYYY-MM-DD. */
  on: string;
};

const FOUND_BY_VALUES: ColegaFoundBy[] = [
  "asesora_proactiva",
  "lead_manager",
  "direccion_corretaje",
];

const toCents = (amount: number): number => Math.round(amount * 100);
const fromCents = (cents: number): number => cents / 100;

/** Resolves the case code from the sale facts; throws on impossible combinations. */
export const resolveCorretajeCase = (
  input: Omit<CorretajeSplitInput, "total" | "on">,
): CorretajeCaseCode => {
  const { sharedWithColega, colegaFoundBy, propertyOwner, opcionadoraKind } =
    input;
  if (propertyOwner !== "almikia" && propertyOwner !== "colega") {
    throw new Error("La propiedad debe ser de Almikia o del colega");
  }
  if (opcionadoraKind !== "advisor" && opcionadoraKind !== "amk") {
    throw new Error("La opcionadora debe ser una asesora o AMK");
  }
  if (colegaFoundBy != null && !FOUND_BY_VALUES.includes(colegaFoundBy)) {
    throw new Error("Quién encontró al colega no es válido");
  }
  if (propertyOwner === "colega" && !sharedWithColega) {
    throw new Error(
      "Una propiedad del colega solo se vende compartida con el colega",
    );
  }
  if (propertyOwner === "colega" && opcionadoraKind === "amk") {
    throw new Error("AMK no puede ser opcionadora de una propiedad del colega");
  }

  if (opcionadoraKind === "amk") {
    return sharedWithColega ? "5" : "2";
  }
  if (!sharedWithColega) {
    return input.sameAdvisor ? "1b" : "1";
  }
  if (colegaFoundBy == null) {
    throw new Error("Indica quién encontró al colega");
  }
  if (colegaFoundBy === "asesora_proactiva") {
    return input.sameAdvisor ? "4b" : "4";
  }
  return input.sameAdvisor ? "3b" : "3";
};

/** The scheme valid on `on` for a case: the latest valid_from wins. */
const findScheme = (
  schemes: CorretajeCommissionScheme[],
  caseCode: CorretajeCaseCode,
  on: string,
): CorretajeCommissionScheme | undefined =>
  schemes
    .filter(
      (scheme) =>
        scheme.case_code === caseCode &&
        scheme.valid_from <= on &&
        (scheme.valid_to == null || scheme.valid_to >= on),
    )
    .sort((a, b) => (a.valid_from < b.valid_from ? 1 : -1))[0];

/**
 * Computes the corretaje commission split. Colega, vendedora and opcionadora
 * are each rounded to cents and Almikia takes the remainder, so the parts add
 * up to exactly `total`.
 *
 * @throws Error with the same message as the SQL function for invalid input,
 * an impossible combination, or a date with no valid scheme.
 */
export const computeCorretajeSplit = (
  input: CorretajeSplitInput,
  schemes: CorretajeCommissionScheme[],
): CorretajeSplitResult => {
  if (!Number.isFinite(input.total) || input.total < 0) {
    throw new Error("La comisión total debe ser un monto igual o mayor a cero");
  }
  const caseCode = resolveCorretajeCase(input);
  const scheme = findScheme(schemes, caseCode, input.on);
  if (!scheme) {
    throw new Error(
      `No hay un esquema de comisión vigente para el caso ${caseCode} el ${input.on}`,
    );
  }

  const totalCents = toCents(input.total);
  const colegaCents = Math.round((totalCents * scheme.colega_pct) / 100);
  const baseCents =
    scheme.base === "total" ? totalCents : totalCents - colegaCents;
  const vendedoraCents = Math.round((baseCents * scheme.vendedora_pct) / 100);
  const opcionadoraCents = Math.round(
    (baseCents * scheme.opcionadora_pct) / 100,
  );

  return {
    case_code: caseCode,
    scheme_id: scheme.id,
    colega_amount: fromCents(colegaCents),
    vendedora_amount: fromCents(vendedoraCents),
    opcionadora_amount: fromCents(opcionadoraCents),
    almikia_amount: fromCents(
      totalCents - colegaCents - vendedoraCents - opcionadoraCents,
    ),
  };
};
